import React, { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Calendar, ArrowRight, Sparkles } from "lucide-react";
import { FaStar } from "react-icons/fa";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

import { useNavigate } from "react-router-dom";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

const HeroSection = () => {
  const navigate = useNavigate();

  // Quick Consultation form state
  const [qcForm, setQcForm] = useState({
    name: "",
    phone: "",
    email: "",
    selectedService: "",
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState("");
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loading, setLoading] = useState(false);

  // Handle input changes
  // Handle input changes
  const handleQcChange = (e) => {
    const { name, value } = e.target;

    // Sanitize name input: allow only letters and spaces
    if (name === "name") {
      const sanitizedName = value.replace(/[^A-Za-z\s]/g, "");
      setQcForm({ ...qcForm, [name]: sanitizedName });
      return;
    }

    // Sanitize phone input: allow only digits and limit to 10
    if (name === "phone") {
      const sanitizedPhone = value.replace(/[^0-9]/g, "").slice(0, 10);
      setQcForm({ ...qcForm, [name]: sanitizedPhone });
      return;
    }

    // Default behavior for other fields
    setQcForm({ ...qcForm, [name]: value });
    setErrors({ ...errors, [name]: "" }); // clear error for that field
  };

  // Handle Quick Consultation form submit
  const handleQuickConsultation = async (e) => {
    e.preventDefault();
    if (loading) return;
    const newErrors = {};

    // Validate name (letters and spaces only)
    if (!qcForm.name.trim()) newErrors.name = "Name is required.";
    else if (!/^[A-Za-z\s]+$/.test(qcForm.name))
      newErrors.name = "Name must contain only letters and spaces.";

    // Validate phone (10-digit number)
    if (!qcForm.phone.trim()) newErrors.phone = "Phone number is required.";
    else if (!/^[0-9]{10}$/.test(qcForm.phone))
      newErrors.phone = "Enter a valid 10-digit phone number.";

    // Validate email
    if (!qcForm.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(qcForm.email)) {
      newErrors.email = "Enter a valid email address.";
    }

    // Validate selected service
    if (!qcForm.selectedService.trim())
      newErrors.selectedService = "Please select a service.";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setLoading(true);
      // Save to Firestore
      await addDoc(collection(db, "quickConsultations"), {
        ...qcForm,
        createdAt: serverTimestamp(),
      });

      // ✅ Send Email via backend
      const res = await fetch(
        "https://vpjc-api-805872224468.asia-south1.run.app/api/consultation/quick-ack",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(qcForm),
        }
      );

      if (!res.ok) throw new Error("Failed to send email");

      // Success
      setSuccess(
        `Your request has been submitted successfully! We’ll contact you soon at ${qcForm.email}`
      );
      setQcForm({ name: "", phone: "", email: "", selectedService: "" });
      setErrors({});
    } catch (err) {
      console.error("Error saving consultation:", err);
      setSuccess("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Hide success message after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Handle Schedule Consultation button
  const handleBookAppointment = () => navigate("/book-appointment");

  // State for booking search
  const [bookingId, setBookingId] = useState("");
  const [bookingData, setBookingData] = useState(null);
  const [searchError, setSearchError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);

  // 🔎 Handle Booking Search
  const handleSearchBooking = async (e) => {
    e.preventDefault();
    setSearchError("");
    setBookingData(null);

    let id = bookingId.trim();
    if (!id) {
      setSearchError("Please enter a booking ID.");
      return;
    }

    // 🔠 Normalize to uppercase
    id = id.toUpperCase();

    setLoadingSearch(true);
    try {
      // 🔍 Search by bookingId field (now uppercase)
      const q = query(
        collection(db, "appointments"),
        where("bookingId", "==", id)
      );
      const qSnap = await getDocs(q);

      if (!qSnap.empty) {
        const d = qSnap.docs[0];
        setBookingData({ id: d.id, ...d.data() });
        setOpenDialog(true);
      } else {
        setSearchError("No booking found with this ID.");
      }
    } catch (err) {
      console.error("Booking search error:", err);
      setSearchError(err?.message || "Something went wrong. Try again.");
    } finally {
      setLoadingSearch(false);
    }
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
  <img
  src="/hero.webp"
  alt="Premium physiotherapy clinic interior"
  className="w-full h-full object-cover"
/>

  <div className="absolute inset-0 bg-gradient-to-r from-[#204080]/90 to-[#2E5AAC]/70"></div>
</div>


      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-white space-y-8">
            {/* Trust Badge */}
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full shadow-md">
              <FaStar className="text-yellow-400" size={20} />
              <span className="text-sm font-semibold text-white">
                22+ Years of Excellence
              </span>
            </div>

            {/* Main Headline */}
            <div className="space-y-4">
              <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
                Expert Physiotherapy
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-white to-blue-200">
                  and Wellness Care
                </span>
              </h1>

              <p className="text-xl lg:text-2xl text-blue-100 leading-relaxed">
                Restoring movement, relieving pain, and uplifting lives through
                personalized treatment and cutting-edge care.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                className="bg-white text-[#204080] hover:bg-blue-50 px-8 py-4 text-lg font-semibold rounded-xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105"
                onClick={handleBookAppointment}
              >
                <Calendar className="mr-2" size={20} />
                Schedule Consultation
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#204080] px-8 py-4 text-lg font-semibold rounded-xl backdrop-blur-sm transition-all duration-300"
                onClick={() => navigate("/services")}
              >
                Get Started
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 pt-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-white">22+</div>
                <div className="text-blue-200 text-sm">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white">1,10,000+</div>
                <div className="text-blue-200 text-sm">Success Stories</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white">98%</div>
                <div className="text-blue-200 text-sm">Recovery Rate</div>
              </div>
            </div>
          </div>

          {/* Right side - floating card */}
          <div className="flex flex-col gap-6">
            {/*Search Bar */}
            {/* <div>
              <form
                onSubmit={handleSearchBooking}
                className="flex flex-col sm:flex-row gap-3 items-center"
              >
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder="Enter Booking ID"
                    value={bookingId}
                    onChange={(e) => setBookingId(e.target.value.toUpperCase())}
                    className="w-full px-4 py-2 pr-10 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50"
                  />
                  {bookingId && (
                    <button
                      type="button"
                      onClick={() => setBookingId("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-white"
                    >
                      ✕
                    </button>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={loadingSearch}
                  className="bg-white text-[#204080] hover:bg-blue-50"
                >
                  {loadingSearch ? "Searching..." : "Search"}
                </Button>
              </form>

              {searchError && (
                <p className="text-red-400 text-sm mt-2">{searchError}</p>
              )}
            </div> */}

            {/* Quick Consultation Card */}
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 shadow-2xl min-h-[400px] flex flex-col justify-start">
              <h2 className="text-white text-2xl font-bold mb-4">
                Quick Consultation
              </h2>

              {success && (
                <p className="text-green-400 text-sm mb-2">{success}</p>
              )}

              <form className="space-y-4" onSubmit={handleQuickConsultation}>
                <div className="flex flex-col">
                  <input
                    type="text"
                    name="name"
                    value={qcForm.name}
                    onChange={handleQcChange}
                    placeholder="Full Name"
                    pattern="[A-Za-z\s]+"
                    className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50"
                  />
                  <p className="text-red-400 text-sm mt-1 min-h-[1rem]">
                    {errors.name}
                  </p>
                </div>

                <div className="flex flex-col">
                  <input
                    type="tel"
                    name="phone"
                    value={qcForm.phone}
                    onChange={handleQcChange}
                    placeholder="Phone Number"
                    pattern="[0-9]*"
                    className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50"
                  />
                  <p className="text-red-400 text-sm mt-1 min-h-[1rem]">
                    {errors.phone}
                  </p>
                </div>

                <div className="flex flex-col">
                  <input
                    type="email"
                    name="email"
                    value={qcForm.email}
                    onChange={handleQcChange}
                    placeholder="Email Address"
                    className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50"
                  />
                  <p className="text-red-400 text-sm mt-1 min-h-[1rem]">
                    {errors.email}
                  </p>
                </div>

                <div className="flex flex-col">
                  <select
                    name="selectedService"
                    value={qcForm.selectedService}
                    onChange={handleQcChange}
                    className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50"
                  >
                    <option value="" className="text-black">
                      Select Service
                    </option>
                    <option value="Rehabilitation" className="text-black">
                      Rehabilitation
                    </option>
                    <option value="Sports Injury" className="text-black">
                      Sports Injury Recovery
                    </option>
                    <option value="Pain Management" className="text-black">
                      Pain Relief & Management
                    </option>
                    <option value="Pain Management" className="text-black">
                      Chiropractic Care
                    </option>
                    <option value="Wellness Program" className="text-black">
                      Customized Wellness Programs
                    </option>
                    <option value="Wellness Program" className="text-black">
                      Corporate Wellness
                    </option>
                  </select>
                  <p className="text-red-400 text-sm mt-1 min-h-[1rem]">
                    {errors.selectedService}
  </p>
</div>


                <Button
                  type="submit"
                  disabled={loading}
                  className={`w-full py-3 font-semibold rounded-lg transition-all duration-300 ${
                    loading
                      ? "bg-gray-300 cursor-not-allowed text-gray-600"
                      : "bg-white text-[#204080] hover:bg-blue-50"
                  }`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <svg
                        className="animate-spin h-5 w-5 text-[#204080]"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8z"
                        />
                      </svg>
                      <span>Submitting...</span>
                    </div>
                  ) : (
                    "Request Callback"
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="backdrop-blur-lg bg-white/90 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#204080]">
              Your Booking Details
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              Here are the details of your appointment. Please keep this
              information safe.
            </DialogDescription>
          </DialogHeader>

          {bookingData ? (
            <div className="space-y-3 mt-4 text-gray-800">
              <p>
                <strong>Booking ID:</strong> {bookingData.bookingId}
              </p>
              <p>
                <strong>Name:</strong> {bookingData.firstName}{" "}
                {bookingData.lastName}
              </p>
              <p>
                <strong>Email:</strong> {bookingData.email}
              </p>
              <p>
                <strong>Phone:</strong> {bookingData.phone}
              </p>
              <p>
                <strong>Date:</strong> {bookingData.date}
              </p>
              <p>
                <strong>Consultation Type:</strong>{" "}
                {bookingData.consultationType}
              </p>
              <p>
                <strong>Branch:</strong> {bookingData.branch},{" "}
                {bookingData.city}
              </p>
              <p>
                <strong>Country:</strong> {bookingData.country}
              </p>
            </div>
          ) : (
            <p className="text-gray-600 mt-4">No details available.</p>
          )}

          <DialogFooter>
            <Button
              onClick={() => setOpenDialog(false)}
              className="bg-[#204080] text-white hover:bg-[#163060]"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scroll indicator */}
      {/* <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white/70 rounded-full mt-2"></div>
        </div>
      </div> */}
    </section>
  );
};

export default HeroSection;
